import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import AssignmentCreator from '../../components/teacher/AssignmentCreator';
import PremiumTooltip from '../../components/common/PremiumTooltip';
import { DocumentTextIcon, PlusIcon, CalendarIcon } from '@heroicons/react/24/outline';
import DeleteAssignmentModal from '../../components/teacher/DeleteAssignmentModal';
import TermSelector from '../../components/common/TermSelector';

const AssignmentList = ({ courseId, initialAssignments = null }) => {
    const { get, del } = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { showToast } = useAppState();
    const [showCreator, setShowCreator] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, assignment: null, loading: false });
    const [selectedTerm, setSelectedTerm] = useState('');

    const { data: assignments = [], isLoading, isFetching } = useQuery({
        queryKey: ['assignments', courseId, selectedTerm],
        queryFn: async () => {
            const termParam = selectedTerm ? `?term=${selectedTerm}` : '';
            const response = await get(`/api/cbc/learning-areas/${courseId}/assignments/${termParam}`);
            return response || [];
        },
        initialData: initialAssignments && initialAssignments.length > 0 && !selectedTerm ? initialAssignments : undefined,
        staleTime: 5 * 60 * 1000,
        enabled: !!courseId,
    });

    // Fetch terms
    const { data: termsData, isLoading: termsLoading } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 60 * 60 * 1000,
    });

    const terms = termsData || [];

    // Set default term if not set
    React.useEffect(() => {
        if (terms.length > 0 && !selectedTerm) {
            const currentTerm = terms.find(t => t.is_current);
            if (currentTerm) {
                setSelectedTerm(currentTerm.id.toString());
            } else {
                setSelectedTerm(terms[0].id.toString());
            }
        }
    }, [terms, selectedTerm]);

    const handleCreateAssignment = () => {
        setEditingAssignment(null);
        setShowCreator(true);
    };

    const handleEditAssignment = (assignment) => {
        setEditingAssignment(assignment);
        setShowCreator(true);
    };

    const handleDeleteAssignment = (assignment) => {
        setDeleteModal({ isOpen: true, assignment, loading: false });
    };

    const confirmDelete = async () => {
        const { assignment } = deleteModal;
        try {
            setDeleteModal(prev => ({ ...prev, loading: true }));
            await del(`/api/assignments/${assignment.id}/`);
            showToast('Assessment deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['assignments', courseId] });
            setDeleteModal({ isOpen: false, assignment: null, loading: false });
        } catch (error) {
            console.error('Error deleting assignment:', error);
            showToast('Failed to delete assessment', 'error');
            setDeleteModal(prev => ({ ...prev, loading: false }));
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending': return 'bg-yellow-100 text-yellow-800';
            case 'Submitted': return 'bg-blue-100 text-blue-800';
            case 'Graded': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const isOverdue = (dueDate) => {
        return new Date(dueDate) < new Date();
    };

    if (isLoading && !assignments.length) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#18216D]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Desktop Header */}
            <div className="hidden md:flex flex-row items-center justify-between gap-6 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm relative overflow-hidden">
                {isFetching && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-indigo-100 overflow-hidden">
                        <div className="w-full h-full bg-[#18216D] animate-progress-buffer origin-left"></div>
                    </div>
                )}
                <div>
                    <h2 className="text-2xl font-black text-[#18216D] tracking-tight">Competency Based Assessment</h2>
                    <p className="text-slate-400 text-sm font-medium">
                        Manage learning area assessments and track submissions
                    </p>
                </div>
                <div className="flex flex-row items-center gap-4">
                    <TermSelector 
                        terms={terms} 
                        selectedTerm={selectedTerm} 
                        onTermChange={setSelectedTerm} 
                        isLoading={isFetching}
                    />
                    <button
                        onClick={handleCreateAssignment}
                        className="px-6 py-3 bg-[#18216D] text-white rounded-xl hover:bg-[#0D164F] transition-all font-black shadow-xl shadow-indigo-900/20 flex items-center space-x-3 text-[10px] uppercase tracking-widest"
                    >
                        <PlusIcon className="w-5 h-5 stroke-[4]" />
                        <span>Create Assessment</span>
                    </button>
                </div>
            </div>

            {/* Mobile Header */}
            <div className="md:hidden space-y-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-1 w-8 bg-[#FFC425] rounded-full"></div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Assessment Registry</p>
                    </div>
                    <h1 className="text-4xl font-black text-[#18216D] tracking-tighter">Competency Based Assessment</h1>
                </div>

                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <TermSelector 
                                terms={terms} 
                                selectedTerm={selectedTerm} 
                                onTermChange={setSelectedTerm} 
                                isLoading={isFetching}
                            />
                        </div>
                        <button
                            onClick={handleCreateAssignment}
                            className="p-4 bg-[#18216D] text-white rounded-2xl shadow-lg shadow-indigo-900/20 active:scale-95 transition-all"
                        >
                            <PlusIcon className="w-6 h-6 stroke-[3]" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Empty State */}
            {assignments.length === 0 ? (
                <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                    <div className="flex justify-center mb-4">
                        <DocumentTextIcon className="h-16 w-16 text-gray-300" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No assessments yet</h3>
                    <p className="text-slate-400 mb-8 font-medium italic">
                        The course workspace is ready. Launch your first assessment to begin assessing student competencies.
                    </p>
                    <button
                        onClick={handleCreateAssignment}
                        className="px-10 py-4 bg-[#18216D] text-white rounded-2xl hover:bg-[#0D164F] transition-all font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-900/20"
                    >
                        Create Your First Assessment
                    </button>
                </div>
            ) : (
                <div className="grid gap-4">
                    {assignments.map((assignment) => (
                        <div key={assignment.id} className="bg-white rounded-xl border border-gray-200 hover:shadow-xl hover:shadow-indigo-900/5 transition-all p-4 md:p-6 group">
                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <h3 className="text-base md:text-lg font-bold text-gray-900 leading-tight">{assignment.title}</h3>
                                        {(() => {
                                            const enrolled = assignment.enrolled_count || 0;
                                            const graded = assignment.graded_submissions_count || 0;
                                            const isGraded = enrolled > 0 ? (graded >= enrolled) : (assignment.status === 'Graded');
                                            
                                            return (
                                                <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-full border ${isGraded ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}>
                                                    {isGraded ? 'Graded' : 'Pending'}
                                                </span>
                                            );
                                        })()}
                                        {assignment.is_manual && (
                                            <span className="px-1.5 py-0.5 text-[9px] font-black bg-slate-100 text-slate-800 rounded-full uppercase tracking-widest border border-slate-200 shadow-sm">
                                                Manual Entry
                                            </span>
                                        )}
                                        {(() => {
                                            const enrolled = assignment.enrolled_count || 0;
                                            const submissions = assignment.submission_count || 0;
                                            if (isOverdue(assignment.due_date) && submissions < enrolled) {
                                                return (
                                                    <span className="px-2 py-0.5 text-[9px] font-black bg-rose-100 text-rose-800 rounded-full border border-rose-200 uppercase tracking-widest">
                                                        Overdue
                                                    </span>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                    <p className="text-sm text-gray-500 mb-4 line-clamp-2 italic font-medium">{assignment.description}</p>
                                    
                                    <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-[11px] md:text-sm text-slate-500 font-bold mb-4">
                                         <div className="flex items-center space-x-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-100">
                                             <CalendarIcon className="w-4 h-4 text-[#18216D]" />
                                             <span>Due: {new Date(assignment.due_date).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                         </div>
                                        <div className="flex items-center space-x-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-100">
                                            <svg className="w-4 h-4 text-[#18216D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span>{assignment.total_marks} marks</span>
                                        </div>
                                        <div className="flex items-center space-x-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-100">
                                            <svg className="w-4 h-4 text-[#18216D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                            <span>{assignment.submission_count || 0} submissions</span>
                                        </div>
                                    </div>

                                    {assignment.tested_outcomes_detail?.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {assignment.tested_outcomes_detail.map((o, i) => (
                                                <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-xl group-hover:bg-white group-hover:border-indigo-100 transition-colors">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-[#FFC425]"></div>
                                                    <span className="text-[10px] font-bold text-[#18216D] italic leading-tight">{o.description}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-end md:justify-start gap-2 pt-4 md:pt-0 border-t md:border-t-0 border-slate-50 mt-2 md:mt-0">
                                    {/* Action button: Grade for Manual, Submissions for Normal */}
                                    <PremiumTooltip text={assignment.is_manual ? "Open Grade Assessment" : "Open submissions"} position="top">
                                        <button
                                            onClick={() => {
                                                if (assignment.is_manual) {
                                                    navigate(`/teacher/courses/${courseId}/grade/${assignment.id}`);
                                                } else {
                                                    navigate(`/teacher/assignments/${assignment.id}/submissions`);
                                                }
                                            }}
                                            className={`p-3 rounded-xl transition-all shadow-sm ${assignment.is_manual ? 'bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white' : 'bg-indigo-50 text-[#18216D] hover:bg-[#18216D] hover:text-white'}`}
                                        >
                                            {assignment.is_manual ? (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            )}
                                        </button>
                                    </PremiumTooltip>
                                    
                                    <PremiumTooltip text="Edit Assessment" position="top">
                                        <button
                                            onClick={() => handleEditAssignment(assignment)}
                                            className="p-3 bg-slate-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-xl transition-all shadow-sm"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                    </PremiumTooltip>
                                    
                                    <PremiumTooltip text="Delete Assessment" position="top">
                                        <button
                                            onClick={() => handleDeleteAssignment(assignment)}
                                            className="p-3 bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white rounded-xl transition-all shadow-sm"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </PremiumTooltip>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Assignment Creator Modal */}
            {showCreator && (
                <AssignmentCreator
                    courseId={courseId}
                    assignment={editingAssignment}
                    onClose={() => {
                        setShowCreator(false);
                        setEditingAssignment(null);
                    }}
                    onSave={() => {
                        queryClient.invalidateQueries({ queryKey: ['assignments', courseId] });
                    }}
                />
            )}

            {/* Deletion Modal */}
            <DeleteAssignmentModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, assignment: null, loading: false })}
                onConfirm={confirmDelete}
                title={deleteModal.assignment?.title}
                loading={deleteModal.loading}
            />
        </div>
    );
};
export default AssignmentList;
